import {
  useCallback,
  type RefObject,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { useValidateAndSanitize } from './useValidateAndSanitize.js';
import { useSelection } from '../../hooks/useSelection.js';
import { NUMBER_SPIN_BUTTON_DEFAULTS } from '../constants.js';
import { NumberSpinButtonProps } from '../types.js';

/**
 * This hook provides a keydown handler to navigate a given value range.
 */
export const useValueNavigation = (
  ref: RefObject<HTMLElement | null> | undefined,
  props: NumberSpinButtonProps,
  applyText?: (value: string, fireOnChange?: boolean) => void,
) => {
  const {
    disabled,
    readOnly,
    onChange,
    displayFormat,
    required,
    minValue,
    maxValue,
    value,
  } = props;
  const { selectAll } = useSelection(ref);
  const { sanitize, format } = useValidateAndSanitize(
    displayFormat,
    required,
    minValue,
    maxValue,
  );

  // Default transformer functions for spinbutton keyboard handlers
  const {
    onArrowUp = (value, e) => {
      e.preventDefault();
      return (value ?? 0) + NUMBER_SPIN_BUTTON_DEFAULTS.SMALL_STEP;
    },
    onArrowDown = (value, e) => {
      e.preventDefault();
      return (value ?? 0) - NUMBER_SPIN_BUTTON_DEFAULTS.SMALL_STEP;
    },
    onPageUp = (value, e) => {
      e.preventDefault();
      return (value ?? 0) + NUMBER_SPIN_BUTTON_DEFAULTS.BIG_STEP;
    },
    onPageDown = (value, e) => {
      e.preventDefault();
      return (value ?? 0) - NUMBER_SPIN_BUTTON_DEFAULTS.BIG_STEP;
    },
    onHome = (value, e) => {
      // Only execute if a minValue prop was defined
      if (minValue !== undefined) {
        e.preventDefault();
        return minValue;
      }
      return value;
    },
    onEnd = (value, e) => {
      // Only execute if a maxValue prop was defined
      if (maxValue !== undefined) {
        e.preventDefault();
        return maxValue;
      }
      return value;
    },
  } = props;

  const onKeyDownHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (disabled || readOnly) {
        // Prevent any value changes via keys, but don't prevent the default behavior.
        return;
      }

      // Ensures, the given value is always sanitized before updating the inner state.
      const updateValue = (newValue: number | null) => {
        const sanitizedValue = sanitize(newValue);
        if (value === sanitizedValue) {
          return;
        }

        const newText = format(sanitizedValue);
        applyText?.(newText);
        onChange?.(sanitizedValue);
        selectAll();
      };

      switch (e.key) {
        case 'ArrowUp':
          updateValue(onArrowUp(value ?? 0, e));
          break;
        case 'ArrowDown':
          updateValue(onArrowDown(value ?? 0, e));
          break;
        case 'PageUp':
          updateValue(onPageUp(value ?? 0, e));
          break;
        case 'PageDown':
          updateValue(onPageDown(value ?? 0, e));
          break;
        case 'Home':
          updateValue(onHome(value ?? 0, e));
          break;
        case 'End':
          updateValue(onEnd(value ?? 0, e));
          break;
      }
    },
    [
      disabled,
      readOnly,
      value,
      format,
      sanitize,
      applyText,
      onChange,
      selectAll,
      onArrowUp,
      onArrowDown,
      onPageUp,
      onPageDown,
      onHome,
      onEnd,
    ],
  );
  return { onKeyDownHandler };
};
